import React, { Suspense, lazy, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building, Users, MapPin, Shield, KeyRound, Building2 } from 'lucide-react';
import { HubShell, type HubTab } from '@/components/common/HubShell';

const RagioneSociale = lazy(() => import('./RagioneSociale'));
const Stores = lazy(() => import('./Stores'));
const UsersPage = lazy(() => import('./Users'));
const SecuritySettings = lazy(() => import('@/pages/impostazioni/SecuritySettings'));
const AuthProviderSettings = lazy(() => import('@/pages/impostazioni/AuthProviderSettings'));

type TabKey = 'ragione-sociale' | 'sedi' | 'users' | 'sicurezza' | 'provider-auth';

const tabs: readonly HubTab<TabKey>[] = [
  { key: 'ragione-sociale', label: 'Ragione Sociale', icon: Building },
  { key: 'sedi', label: 'Sedi', icon: MapPin },
  { key: 'users', label: 'Users', icon: Users },
  { key: 'sicurezza', label: 'Sicurezza', icon: Shield },
  { key: 'provider-auth', label: 'Provider Auth', icon: KeyRound },
];

const tabComponents: Record<TabKey, React.LazyExoticComponent<React.ComponentType>> = {
  'ragione-sociale': RagioneSociale,
  'sedi': Stores,
  'users': UsersPage,
  'sicurezza': SecuritySettings,
  'provider-auth': AuthProviderSettings,
};

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

export default function TenantManagementPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') || 'ragione-sociale';
  const activeTab = tabs.some(t => t.key === rawTab) ? (rawTab as TabKey) : 'ragione-sociale';

  const handleTabChange = useCallback((tabKey: TabKey) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tabKey);
    const keysToRemove: string[] = [];
    newParams.forEach((_, key) => {
      if (key !== 'tab') keysToRemove.push(key);
    });
    keysToRemove.forEach(k => newParams.delete(k));
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const ActiveComponent = tabComponents[activeTab] || RagioneSociale;

  return (
    <HubShell
      icon={Building2}
      title="Gestione Organizzazione"
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      mainClassName="flex-1 overflow-auto"
    >
      <Suspense fallback={<LoadingSpinner />}>
        <ActiveComponent />
      </Suspense>
    </HubShell>
  );
}
